import type { FC, ReactNode } from 'react'
import type { PricingFeatureRow, PricingPlan } from '@/types'

import { Fragment } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'

type PlanKey = 'starter' | 'growth' | 'enterprise'

const PLAN_KEYS: Array<PlanKey> = ['starter', 'growth', 'enterprise']

interface ComparisonTableProps {
    rows: Array<PricingFeatureRow>
    plans: Array<PricingPlan>
}

const Cell: FC<{ value: string | null }> = ({ value }): ReactNode => {
    const normalized = (value ?? '').trim().toLowerCase()

    if (['yes', 'true', 'included', 'y'].includes(normalized)) {
        return (
            <>
                <Check aria-hidden='true' className='mx-auto size-4 text-burgundy' />
                <span className='sr-only'>Included</span>
            </>
        )
    }

    if (!normalized || ['no', 'false', 'n', '-'].includes(normalized)) {
        return (
            <>
                <Minus aria-hidden='true' className='mx-auto size-4 text-stone-light' />
                <span className='sr-only'>Not included</span>
            </>
        )
    }

    return <span className='text-sm text-burgundy'>{value}</span>
}

export const ComparisonTable: FC<ComparisonTableProps> = ({ rows, plans }): ReactNode => {
    if (!rows.length) return null

    const planFor = (key: PlanKey) => plans.find(plan => plan.slug === key)
    const groups = rows.reduce<Array<{ name: string, rows: Array<PricingFeatureRow> }>>((list, row) => {
        const existing = list.find(group => group.name === row.group)

        if (existing) existing.rows.push(row)
        else list.push({ name: row.group, rows: [row] })

        return list
    }, [])

    return (
        <div className='overflow-x-auto rounded-[28px] border border-burgundy/10 bg-ivory'>
            <table className='w-full min-w-[720px] border-collapse text-left'>
                <caption className='sr-only'>Feature comparison across Veyra plans</caption>

                <thead>
                    <tr className='border-b border-burgundy/10'>
                        <th scope='col' className='w-[34%] p-6 align-bottom'>
                            <span className='font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone'>Compare plans</span>
                        </th>

                        {PLAN_KEYS.map(key => {
                            const plan = planFor(key)

                            return (
                                <th key={key} scope='col' className={cn('p-6 text-center align-bottom', plan?.highlighted && 'bg-burgundy/[0.04]')}>
                                    <span className='block font-heading text-xl font-medium text-burgundy'>{plan?.name ?? key[0].toUpperCase() + key.slice(1)}</span>

                                    {plan && (
                                        <ButtonLink
                                            href={plan.ctaHref}
                                            variant={plan.highlighted ? 'primary' : 'secondary'}
                                            size='sm'
                                            className='mt-4'
                                        >
                                            {plan.ctaLabel}
                                        </ButtonLink>
                                    )}
                                </th>
                            )
                        })}
                    </tr>
                </thead>

                <tbody>
                    {groups.map(group => (
                        <Fragment key={group.name}>
                            <tr>
                                <th
                                    scope='colgroup'
                                    colSpan={4}
                                    className='bg-paper px-6 pb-3 pt-8 font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-stone-dark'
                                >
                                    {group.name}
                                </th>
                            </tr>

                            {group.rows.map(row => (
                                <tr key={row.id} className='border-t border-burgundy/[0.07]'>
                                    <th scope='row' className='px-6 py-4 text-sm font-normal text-stone-dark'>{row.feature}</th>

                                    {PLAN_KEYS.map(key => (
                                        <td key={key} className={cn('px-6 py-4 text-center', planFor(key)?.highlighted && 'bg-burgundy/[0.04]')}>
                                            <Cell value={row[key]} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
